import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialsSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Mirco Lehr",
      position: "Rechtsanwalt",
      avatar: "/assets/images/ML-Portrat-neu-1788637393994.jpeg",
      rating: 5,
      content: `Die Flexibilität bei AdvoCall hat mein Berufsleben revolutioniert. Ich kann von überall arbeiten und meine Arbeitszeiten selbst bestimmen. Die Vergütung ist überdurchschnittlich und die Kollegen sind sehr unterstützend.\n\nBesonders schätze ich die kontinuierlichen Fortbildungsmöglichkeiten und die moderne Technologie, die uns zur Verfügung gestellt wird.`,
      highlights: ["Flexible Arbeitszeiten", "Überdurchschnittliche Vergütung", "Fortbildungen"]
    }
  ];

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials?.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
  };

  const currentTestimonial = testimonials?.[activeTestimonial];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Was unsere Anwälte sagen
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Erfahren Sie aus erster Hand, wie es ist, bei AdvoCall zu arbeiten und Teil unseres innovativen Teams zu sein
          </p>
        </div>

        <div className="relative">
          <div className="bg-gradient-to-br from-surface to-white rounded-3xl p-8 lg:p-12 shadow-elevated">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Testimonial Content */}
              <div className="space-y-6">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(currentTestimonial?.rating)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={20} color="var(--color-warning)" className="fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-lg text-text-secondary leading-relaxed">
                  {currentTestimonial?.content?.split('\n\n')?.map((paragraph, index) => (
                    <p key={index} className={index > 0 ? 'mt-4' : ''}>
                      "{paragraph}"
                    </p>
                  ))}
                </blockquote>

                <div className="flex flex-wrap gap-2">
                  {currentTestimonial?.highlights?.map((highlight, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center space-x-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium"
                    >
                      <Icon name="Check" size={14} />
                      <span>{highlight}</span>
                    </span>
                  ))}
                </div>

                <div className="flex items-center space-x-4 pt-6 border-t border-slate-200">
                  <div className="w-16 h-16 rounded-full overflow-hidden">
                    <Image
                      src={currentTestimonial?.avatar}
                      alt={currentTestimonial?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-text-primary">
                      {currentTestimonial?.name}
                    </h4>
                    <p className="text-text-secondary">
                      {currentTestimonial?.position}
                    </p>
                  </div>
                </div>
              </div>

              {/* Visual Elements */}
              <div className="relative">
                <div className="bg-gradient-to-br from-primary to-accent rounded-2xl p-8 text-white">
                  <div className="text-center mb-8">
                    <Icon name="Quote" size={48} color="white" className="mx-auto mb-4 opacity-50" />
                    <h3 className="text-2xl font-bold mb-2">Arbeiten bei AdvoCall</h3>
                    <p className="text-white/90">Flexibel, professionell und ortsunabhängig</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Icon name="CheckCircle" size={20} color="white" />
                      <span className="text-white/90">Flexible Arbeitszeiten</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="CheckCircle" size={20} color="white" />
                      <span className="text-white/90">Home Office möglich</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="CheckCircle" size={20} color="white" />
                      <span className="text-white/90">Pauschale Vergütung pro Telefonat</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="CheckCircle" size={20} color="white" />
                      <span className="text-white/90">Regelmäßige Fortbildungen</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <button
              onClick={prevTestimonial}
              className="w-12 h-12 bg-white rounded-full shadow-professional flex items-center justify-center text-text-secondary hover:text-primary hover:shadow-elevated transition-all duration-200"
              aria-label="Vorheriges Testimonial"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>

            <div className="flex space-x-2">
              {testimonials?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === activeTestimonial
                      ? 'bg-primary scale-125' :'bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={`Testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="w-12 h-12 bg-white rounded-full shadow-professional flex items-center justify-center text-text-secondary hover:text-primary hover:shadow-elevated transition-all duration-200"
              aria-label="Nächstes Testimonial"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;